import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, 
  Search, 
  Trash2, 
  MessageSquare, 
  Shield, 
  Award,
  UserX,
  AlertTriangle,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { backfillReferralCodes } from '@/utils/referralCodeUtils';

interface UserAnalytics {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role: string;
  account_status: string;
  created_at: string;
  total_points?: number;
  referral_code?: string;
  referred_by?: string;
  chat_count: number;
  block_requests: number;
  user_type: 'supabase' | 'internal';
}

interface UserAnalyticsProps {
  onBack: () => void;
}

const UserAnalytics: React.FC<UserAnalyticsProps> = ({ onBack }) => {
  const [users, setUsers] = useState<UserAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserAnalytics[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeStatFilter, setActiveStatFilter] = useState<string | null>(null);

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'player', label: 'Player' },
    { value: 'coach', label: 'Coach' },
    { value: 'admin', label: 'Admin' },
    { value: 'team-owner', label: 'Team Owner' },
    { value: 'venue', label: 'Venue' },
    { value: 'creative', label: 'Creative' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'pending-approval', label: 'Pending Approval' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'disabled', label: 'Disabled' },
  ];

  useEffect(() => {
    loadUserAnalytics();
  }, []);

  useEffect(() => {
    let filtered = users;

    // Apply search query filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.role.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => 
        user.role.toLowerCase() === roleFilter.toLowerCase()
      );
    }

    // Apply status filter (handle inactive special case)
    if (statusFilter !== 'all') {
      if (activeStatFilter === 'inactive') {
        filtered = filtered.filter(user => 
          ['inactive', 'disabled', 'suspended'].includes(user.account_status.toLowerCase())
        );
      } else {
        filtered = filtered.filter(user => 
          user.account_status.toLowerCase() === statusFilter.toLowerCase()
        );
      }
    }

    // Apply referral code filter
    if (activeStatFilter === 'referral') {
      filtered = filtered.filter(user => 
        user.referral_code && user.referral_code.trim() !== ''
      );
    }

    setFilteredUsers(filtered);
  }, [searchQuery, users, roleFilter, statusFilter, activeStatFilter]);

  const handleStatCardClick = (filterType: string) => {
    // Toggle off if clicking same filter
    if (activeStatFilter === filterType) {
      setActiveStatFilter(null);
      setStatusFilter('all');
      setRoleFilter('all');
      return;
    }
    
    setActiveStatFilter(filterType);
    setRoleFilter('all');
    
    switch (filterType) {
      case 'active':
        setStatusFilter('active');
        break;
      case 'pending':
        setStatusFilter('pending-approval');
        break;
      case 'inactive':
        setStatusFilter('inactive');
        break;
      case 'total':
        setStatusFilter('all');
        setActiveStatFilter(null);
        break;
      case 'referral':
        setStatusFilter('all');
        break;
    }
  };

  const handleBackfillReferralCodes = async () => {
    setBackfilling(true);
    try {
      await backfillReferralCodes();
      toast.success('Referral codes backfilled successfully');
      // Reload user analytics to show updated data
      await loadUserAnalytics();
    } catch (error) {
      console.error('Error backfilling referral codes:', error);
      toast.error('Failed to backfill referral codes');
    } finally {
      setBackfilling(false);
    }
  };

  const loadUserAnalytics = async () => {
    try {
      setLoading(true);
      
      // Get Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('*');

      if (supabaseError) throw supabaseError;

      // Get Internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_users')
        .select('*');

      if (internalError) throw internalError;

      // Get points data
      const { data: pointsData, error: pointsError } = await supabase
        .from('user_points')
        .select('*');

      if (pointsError) throw pointsError;

      // Combine and process user data
      const combinedUsers: UserAnalytics[] = [];

      // Process Supabase users
      if (supabaseUsers) {
        for (const user of supabaseUsers) {
          const userPoints = pointsData?.find(p => p.user_id === user.id && p.user_type === 'supabase');
          
          combinedUsers.push({
            id: user.id,
            username: user.username,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            role: user.role || 'player',
            account_status: user.account_status || 'active',
            created_at: user.created_at,
            total_points: userPoints?.total_points || 0,
            referral_code: userPoints?.referral_code,
            referred_by: userPoints?.referred_by,
            chat_count: 0, // Will be populated by actual chat data
            block_requests: 0, // Will be populated by security incident data
            user_type: 'supabase'
          });
        }
      }

      // Process Internal users
      if (internalUsers) {
        for (const user of internalUsers) {
          const userPoints = pointsData?.find(p => p.user_id === user.id && p.user_type === 'internal');
          
          combinedUsers.push({
            id: user.id,
            username: user.username,
            first_name: user.username, // Internal users use username as display name
            last_name: '',
            email: '', // Internal users don't have email in this table
            role: user.role,
            account_status: user.is_active ? 'active' : 'inactive',
            created_at: user.created_at,
            total_points: userPoints?.total_points || 0,
            referral_code: userPoints?.referral_code,
            referred_by: userPoints?.referred_by,
            chat_count: 0,
            block_requests: 0,
            user_type: 'internal'
          });
        }
      }

      setUsers(combinedUsers);
      setFilteredUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading user analytics:', error);
      toast.error('Failed to load user analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userType: 'supabase' | 'internal') => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      if (userType === 'supabase') {
        // For Supabase users, we'd typically deactivate rather than delete
        const { error } = await supabase
          .from('profiles')
          .update({ account_status: 'disabled' })
          .eq('id', userId);
        
        if (error) throw error;
        toast.success('User account disabled');
      } else {
        // For internal users, set as inactive
        const { error } = await supabase
          .from('internal_users')
          .update({ is_active: false })
          .eq('id', userId);
        
        if (error) throw error;
        toast.success('Internal user deactivated');
      }
      
      // Reload data
      loadUserAnalytics();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-500';
      case 'pending-approval': return 'bg-yellow-500';
      case 'suspended': return 'bg-red-500';
      case 'disabled': return 'bg-gray-500';
      case 'inactive': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin': return 'bg-red-500';
      case 'coach': return 'bg-blue-500';
      case 'team-owner': return 'bg-purple-500';
      case 'venue': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading user analytics...</div>
      </div>
    );
  }

  const activeUsers = filteredUsers.filter(u => u.account_status === 'active').length;
  const pendingUsers = filteredUsers.filter(u => u.account_status === 'pending-approval').length;
  const inactiveUsers = filteredUsers.filter(u => ['inactive', 'disabled', 'suspended'].includes(u.account_status)).length;
  const totalUsers = filteredUsers.length;
  const usersWithReferralCodes = filteredUsers.filter(u => u.referral_code && u.referral_code !== '').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin Panel
          </Button>
          <h1 className="text-2xl font-bold text-white">User Analytics Dashboard</h1>
        </div>
        
        <Button
          onClick={handleBackfillReferralCodes}
          disabled={backfilling}
          className="bg-sports-green text-black hover:bg-green-600"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
          {backfilling ? 'Backfilling...' : 'Backfill Referral Codes'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card 
          className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
            activeStatFilter === 'active' 
              ? 'border-green-400 ring-2 ring-green-400/50' 
              : 'hover:border-green-400/50'
          }`}
          onClick={() => handleStatCardClick('active')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Active Users</p>
                <p className="text-2xl font-bold text-green-400">{activeUsers}</p>
              </div>
              <Users className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
            activeStatFilter === 'pending' 
              ? 'border-yellow-400 ring-2 ring-yellow-400/50' 
              : 'hover:border-yellow-400/50'
          }`}
          onClick={() => handleStatCardClick('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-400">{pendingUsers}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
            activeStatFilter === 'inactive' 
              ? 'border-red-400 ring-2 ring-red-400/50' 
              : 'hover:border-red-400/50'
          }`}
          onClick={() => handleStatCardClick('inactive')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Inactive Users</p>
                <p className="text-2xl font-bold text-red-400">{inactiveUsers}</p>
              </div>
              <UserX className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
            activeStatFilter === 'total' 
              ? 'border-blue-400 ring-2 ring-blue-400/50' 
              : 'hover:border-blue-400/50'
          }`}
          onClick={() => handleStatCardClick('total')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Users</p>
                <p className="text-2xl font-bold text-blue-400">{totalUsers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`bg-gray-800 border-gray-700 cursor-pointer transition-all ${
            activeStatFilter === 'referral' 
              ? 'border-sports-green ring-2 ring-sports-green/50' 
              : 'hover:border-sports-green/50'
          }`}
          onClick={() => handleStatCardClick('referral')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">With Referral Codes</p>
                <p className="text-2xl font-bold text-sports-green">{usersWithReferralCodes}</p>
                <p className="text-xs text-gray-500">of {users.length}</p>
              </div>
              <Award className="h-8 w-8 text-sports-green" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name, username, email..."
                className="bg-gray-700 border-gray-600 text-white pl-10"
              />
            </div>
            
            {/* Role Filter */}
            <div className="w-full md:w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {roleOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="text-white hover:bg-gray-700 focus:bg-gray-700"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {statusOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="text-white hover:bg-gray-700 focus:bg-gray-700"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-300">User</TableHead>
                  <TableHead className="text-gray-300">Role</TableHead>
                  <TableHead className="text-gray-300">Status</TableHead>
                  <TableHead className="text-gray-300">Points</TableHead>
                  <TableHead className="text-gray-300">Referral Code</TableHead>
                  <TableHead className="text-gray-300">Chats</TableHead>
                  <TableHead className="text-gray-300">Blocks</TableHead>
                  <TableHead className="text-gray-300">Type</TableHead>
                  <TableHead className="text-gray-300">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="border-gray-700">
                    <TableCell>
                      <div>
                        <div className="font-medium text-white">
                          {user.username || `${user.first_name} ${user.last_name}`.trim() || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-400">{user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getRoleColor(user.role)} text-white`}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(user.account_status)} text-white`}>
                        {user.account_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Award className="h-4 w-4 text-yellow-400" />
                        <span className="text-white">{user.total_points || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.referral_code ? (
                        <code className="text-sports-green text-sm bg-gray-700 px-2 py-1 rounded">
                          {user.referral_code}
                        </code>
                      ) : (
                        <span className="text-red-400 text-sm">Missing</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-4 w-4 text-blue-400" />
                        <span className="text-white">{user.chat_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Shield className="h-4 w-4 text-red-400" />
                        <span className="text-white">{user.block_requests}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-gray-600 text-gray-300">
                        {user.user_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleDeleteUser(user.id, user.user_type)}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserAnalytics;
